import type { InvitationTokenPayload } from '~/types';

/**
 * Decode URL-safe base64 string.
 * Handles `-` → `+` and `_` → `/` conversions.
 */
function base64UrlDecode(str: string): string {
  // Replace URL-safe characters
  let base64 = str.replace(/-/g, '+').replace(/_/g, '/');

  // Add padding if needed
  const padding = base64.length % 4;
  if (padding) {
    base64 += '='.repeat(4 - padding);
  }

  try {
    return globalThis.atob(base64);
  } catch {
    return '';
  }
}

/**
 * Get expiration date from token.
 *
 * @param token - JWT token string
 * @returns Date object or null if invalid
 */
export function getTokenExpirationDate(token: string): Date | null {
  const payload = parseInvitationToken(token);
  if (!payload) {
    return null;
  }

  return new Date(payload.exp * 1000);
}

/**
 * Get token payload (convenience wrapper).
 * Same as parseInvitationToken but with clearer naming.
 *
 * @param token - JWT token string
 * @returns Token payload or null if invalid
 */
export function getTokenPayload(token: string): InvitationTokenPayload | null {
  return parseInvitationToken(token);
}

/**
 * Check if a token is expired (or will expire within buffer period).
 *
 * @param token - JWT token string
 * @param bufferSeconds - Buffer time before actual expiry (default 60s)
 * @returns true if expired or will expire within buffer, false otherwise
 */
export function isTokenExpired(token: string, bufferSeconds = 60): boolean {
  const payload = parseInvitationToken(token);
  if (!payload) {
    // Treat malformed tokens as expired
    return true;
  }

  const nowSeconds = Math.floor(Date.now() / 1000);
  const expiresAt = payload.exp;

  return nowSeconds >= expiresAt - bufferSeconds;
}

/**
 * Parse a JWT invitation token and extract its payload.
 * Does NOT verify the signature - only decodes.
 *
 * @param token - JWT token string
 * @returns Parsed payload or null if malformed
 */
export function parseInvitationToken(token: string): InvitationTokenPayload | null {
  if (!token || typeof token !== 'string') {
    return null;
  }

  const parts = token.split('.');
  if (parts.length !== 3) {
    return null;
  }

  try {
    const payloadStr = base64UrlDecode(parts[1]);
    if (!payloadStr) {
      return null;
    }

    const payload = JSON.parse(payloadStr) as Record<string, unknown>;

    // Validate required fields
    if (
      typeof payload['invitation_id'] !== 'string' ||
      typeof payload['org_id'] !== 'string' ||
      typeof payload['email'] !== 'string' ||
      typeof payload['exp'] !== 'number' ||
      typeof payload['iat'] !== 'number' ||
      payload['type'] !== 'organization_invitation'
    ) {
      return null;
    }

    return payload as unknown as InvitationTokenPayload;
  } catch {
    return null;
  }
}
